/**
 * What: The one banner in the Study Builder. Every notice the browser raises before (or around) a Worker answer — a load
 *   error, the newer-saved-version chooser, the started-test notice, the direct-study status, a product-picker read failure
 *   — is drawn here, so `s-banner` lives in exactly one place. It owns `tone`, `heading` and pass-through data attributes;
 *   the caller supplies the lines and buttons as children.
 * Transport / data-flow: Pure presentation. The ratchet `no-own-section-frame-in-builder-ui` forbids a raw `s-banner`
 *   anywhere else in the builder, so this component (and `SectionFrame` for headings) are the builder's only chrome owners.
 */
import type { ReactNode } from 'react'

type BannerTone = 'critical' | 'warning' | 'info' | 'success'
type DataAttributes = Record<`data-${string}`, string>

export function Banner({
  tone,
  heading,
  attributes,
  children,
}: {
  /** The banner's tone; omitted for the neutral (default) banner. */
  tone?: BannerTone
  /** The bold heading line; omitted when the banner has none. */
  heading?: string
  /** Extra data-* attributes for the banner (e.g. a page-error marker read by tests). */
  attributes?: DataAttributes
  children: ReactNode
}) {
  return (
    <s-banner tone={tone} heading={heading} {...attributes}>
      {children}
    </s-banner>
  )
}
